import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ActiveTeamService} from "../service/active-team.service";
import {catchError, concatMap, of, Subject, Subscription, takeUntil} from "rxjs";
import {TeamService} from "../service/team.service";
import {Team} from "../interface/team";
import {HttpErrorResponse} from "@angular/common/http";
import {MatDialog} from "@angular/material/dialog";
import {SharedTeamNameChangeService} from "../service/team-name-change.service";
import {AddNewMemberInTeamComponent} from "./add-new-member-in-team/add-new-member-in-team.component";
import {UserCardResponse} from "../interface/userCardResponse";
import {UserCardService} from "../service/user-card.service";
import {ViewStateService} from "../service/view-state.service";
import {UserCardResponseWrap} from "../interface/userCardResponseWrap";
import {TeamNotificationService} from "../service/team-notification-service";
import {DeleteCardFromTeamComponent} from "./delete-card-from-team/delete-card-from-team.component";
import {HeaderDataService} from "../service/header-data-service";
import {ActivatedRoute, Router} from "@angular/router";
import {HeaderFactoryService} from "../header/header-factory.service";
import {PaymentBackUrlParamsHandlerService} from "../subscription-payment/payment-back-url-params-handler.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {MatSnackBar} from "@angular/material/snack-bar";
import {PageableDefault} from "../models/pageableDefault";
import {CardsSortDialogService} from "../cards-sort-dialog/cards-sort-dialog.service";
import {CardsSortEnum, CardsSortUtil} from "../cards-sort-dialog/cards-sort.enum";
import {SubscriptionService} from "../service/subscription.service";
import {SubscriptionResponse} from "../interface/subscriptionResponse";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {TEAM_SUBSCRIPTION_EXPIRY_ERROR_ALERT} from "../constants/global.constants";
import {FavoriteCardsService} from "../service/favorite-cards.service";
import {SidebarComponent} from "../sidebar/sidebar.component";
import {
  TeamTransactionPermissionModalComponent
} from "./team-transaction-permission-modal/team-transaction-permission-modal.component";
import {SharedConstants} from "../utilityClass/sharedConstants";
import {Page} from "../interface/hashtag";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-teams',
  templateUrl: './teams.component.html',
  styleUrls: ['./teams.component.scss',
    'teams.component-media.scss',
    '../header/header.component.scss',
  ],
})
export class TeamsComponent implements OnInit, OnDestroy {

  @Output() activeReservedQrCard = new EventEmitter<string>();

  selectedCardId: string | null = null;

  itemsPerPage: number = 3;
  minPageCountForNavigation = 4;
  currentPage: number = 0;

  currentUserId: bigint | any;
  currentUserEmail: string | any;

  activeTeamIndex: number | any;
  subscription: Subscription | any;
  userCreatedTeam: Team | any = null;
  teams: Team[] | any = [];

  cards: UserCardResponse[] = [];
  pageUserCards!: Page<UserCardResponse>
  wrappedCards: UserCardResponseWrap[] = [];
  currentCardPage: number = 0;
  cardPageSize: number = 6;
  totalElementCount: number | any;

  isListView: boolean = false;
  isFavoriteMap: { [key: string]: boolean } = {};

  isEditTeamNameActive: boolean = false;
  editTeamNameForm: FormGroup;
  activeSubscription!: SubscriptionResponse;

  reservedIdentificationId: string | null = null;

  isLoading: boolean = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private activeTeamService: ActiveTeamService,
    private teamService: TeamService,
    private dialog: MatDialog,
    private sharedService: SharedTeamNameChangeService,
    private userCardService: UserCardService,
    private viewStateService: ViewStateService,
    private teamNotificationService: TeamNotificationService,
    private headerDataService: HeaderDataService,
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private snackBarUtil: SnackBarUtil,
    private paymentBackUrlParamsHandler: PaymentBackUrlParamsHandlerService,
    private subscriptionService: SubscriptionService,
    private favoriteCardsService: FavoriteCardsService,
    private translateService: TranslateService,
  ) {
    this.paymentBackUrlParamsHandler.handleQueryParams();
    this.editTeamNameForm = this.formBuilder.group({
      newName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(15)]]
    });
    PaymentBackUrlParamsHandlerService.paymentSuccess.subscribe(() => {
      this.subscribeToActiveTeamIndex();
    });
  }

  public getTranslatedTeamName(teamName: string): string {
    return teamName === SharedConstants.MY_TEAM
      ? this.translateService.instant('my_team.my_team')
      : teamName;
  }

  ngOnInit(): void {
    const reservedFromState = history.state?.reservedIdentificationId || null;
    if (reservedFromState) {
      this.router.navigate(['/cards'], {
        state: {reservedIdentificationId: reservedFromState},
        replaceUrl: true
      });
      return;
    }
    this.reservedIdentificationId = null;
    this.activeTeamIndex = this.activeTeamService.getActiveTeamIndex()
    this.subscribeToListViewState();
    this.loadUserTeamsAndNavigate();
    this.subscribeToDeleteNotification();
    this.subscribeToChangeActivityNotification();
    this.subscribeToHeaderData();
    this.subscribeToActiveTeamIndex();
    CardsSortDialogService.sort.subscribe({
      next: (sort: CardsSortEnum) => {
        const pageableDefault = new PageableDefault(
          0,
          this.cardPageSize,
          CardsSortUtil.getSortFieldBySort(sort),
          CardsSortUtil.getSortDirectionBySort(sort)
        );
        this.searchCardsByTeamId(Number(this.activeTeamService.getActiveTeamIndex()), pageableDefault);
      }
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onActiveReservedQrCard(cardId: bigint): void {
    if (!this.cards || !Array.isArray(this.cards)) {
      return;
    }

    this.cards.forEach((card: UserCardResponse) => {
      card.isReservedCardActive = (card.id === cardId);
    });

    const cardIdStr = cardId.toString();
    this.selectedCardId = cardIdStr;
    this.activeReservedQrCard.emit(cardIdStr);
  }

  nextPage(): void {
    if (this.currentPage < this.maxPage) {
      this.currentPage++;
    }
  }

  prevPage(): void {
    this.currentPage = 0;
  }

  scrollToTop(): void {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  navigateByUrl(route: string): void {
    this.router.navigateByUrl(route);
  }

  editTeamName(): void {
    if (this.editTeamNameForm.valid) {
      const newName = this.editTeamNameForm.value.newName;
      if (newName === this.userCreatedTeam.name) {
        this.isEditTeamNameActive = false;
        return;
      }
      this.teamService.updateTeamName(this.userCreatedTeam.id, newName).subscribe({
        next: () => {
          this.userCreatedTeam.name = newName;
          this.sharedService.setTeamName(newName);
          this.isEditTeamNameActive = false;
        },
        error: () => {
          const data = MockupDataModel.error(
            this.translateService.instant('already_exists_error_text')
          );
        }
      });
    } else {
      const data = MockupDataModel.error(
        this.translateService.instant('wrong_name_long_error_text')
      );
      this.showSnackbar(data, SnackBarUtil.CLASSES.error);
    }
  }

  openEditDialogForMemberAdd(): void {
    const teamOwnerId = this.userCreatedTeam?.owner?.id;
    this.subscriptionService.checkTeamSubscription(teamOwnerId).subscribe({
      next: (isTeamSubscription) => {
        if (isTeamSubscription) {
          const dialogRef = this.dialog.open(AddNewMemberInTeamComponent, {
            data: {
              userCreatedTeam: this.userCreatedTeam
            }
          });


        } else {
          this.showSnackbar(MockupDataModel.error(this.translateService.instant('invite_limit_error')), SnackBarUtil.CLASSES.error);
        }
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 409) {
          this.showSnackbar(MockupDataModel.error(this.translateService.instant('invite_limit_error')), SnackBarUtil.CLASSES.error)
        } else if (error.status === 404) {
          this.router.navigate(['/subscriptions'], {state: {isShowAllSubscriptions: true, isIndividualActive: false}});
          this.showSnackbar(MockupDataModel.error(TEAM_SUBSCRIPTION_EXPIRY_ERROR_ALERT), SnackBarUtil.CLASSES.error)
        }
      }
    });
  }

  setActiveTeam(index: number) {
    if (this.activeTeamIndex === index) {
      return;
    }
    this.activeTeamIndex = index;
    const savedIndex = localStorage.getItem(SharedConstants.ACTIVE_TEAM_INDEX);
    if (savedIndex !== null) {
      this.activeTeamIndex = Number(savedIndex);
      this.activeTeamService.setActiveTeamIndex(this.activeTeamIndex);
      index =  this.activeTeamIndex
    }
    this.activeTeamService.setActiveTeamIndex(this.activeTeamIndex);
    this.currentCardPage = 0;
    const pageableDefault = this.createPageableWithSort()
    this.searchCardsByTeamId(index, pageableDefault);
    this.scrollToTop()
  }

  deleteActiveTeamIndex() {
    localStorage.removeItem(SharedConstants.ACTIVE_TEAM_INDEX)
  }

  updateTeam(index: number) {
    localStorage.setItem(SharedConstants.ACTIVE_TEAM_INDEX, index.toString());
  }

  loadMore() {
    this.isLoading = true;
    if (this.pageUserCards?.totalElements > this.cardPageSize) {
      const pageableDefault = new PageableDefault(++this.currentCardPage, this.cardPageSize);
      this.searchCardsByAddingToExisting(this.activeTeamIndex, pageableDefault);
    }
  }

  deleteCardFromTeam(cardId: string) {
    const dialogRef = this.dialog.open(DeleteCardFromTeamComponent, {
      data: {
        cardId: cardId
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.currentCardPage = 0;
        this.reloadCards();
      }
    });
  }

  isDeleteButtonActive(userCardResponse: UserCardResponse): boolean {
    if (this.userCreatedTeam === null) {
      return false;
    } else if (this.activeTeamIndex !== this.userCreatedTeam.id) {
      return false;
    } else {
      return (userCardResponse.userId !== this.currentUserId || userCardResponse.isDuplicated);
    }
  }

  isEditSectionVisibleForGird(userCardResponse: UserCardResponse): boolean {
    if (this.userCreatedTeam !== null) {
      if (this.activeTeamIndex === this.userCreatedTeam.id) {
        return true;
      } else {
        return (userCardResponse.userId === this.currentUserId);
      }
    } else {
      return (userCardResponse.userId === this.currentUserId);
    }
  }

  isEditSectionVisibleForList(): boolean {
    if (this.userCreatedTeam !== null) {
      return this.activeTeamIndex === this.userCreatedTeam.id;
    } else {
      return false;
    }
  }

  changeCardActivity(id: bigint) {
    this.userCardService.changeActivationStatus(id).subscribe(() => {
      const userCardById = this.wrappedCards.find((card: UserCardResponseWrap) => card.userCardResponse.id === id);
      if (userCardById) {
        userCardById.userCardResponse.active = !userCardById.userCardResponse.active;
      }
    });
  }

  isAdminCard(userId: bigint): boolean {
    return this.isOwnerOfActiveTeam(userId) || this.isOwnerOfUserCreatedTeam(userId);
  }

  get visibleTeams(): Team[] {
    const startIndex = this.currentPage * this.itemsPerPage;
    return this.teams.slice(startIndex, startIndex + this.itemsPerPage);
  }

  get getAllTeams(): Team[] {
    return this.teams;
  }

  get maxPage() {
    return Math.ceil(this.teams.length / this.itemsPerPage) - 1;
  }

  @HostListener('window:scroll', ['$event'])
  private onScroll() {
    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.isLoading) {
      this.loadMore();
    }
  }

  addNewCard(newCard: UserCardResponse): void {
    const newWrappedCards = {userCardResponse: newCard};
    this.wrappedCards.push(newWrappedCards);
  }

  private isOwnerOfActiveTeam(userId: bigint): boolean {
    return this.teams.some((team: Team) => {
      return this.activeTeamIndex === team.id && team.owner.id === userId;
    });
  }

  private isOwnerOfUserCreatedTeam(userId: bigint): boolean {
    return (
      this.userCreatedTeam !== null &&
      this.userCreatedTeam.owner.id === userId &&
      this.userCreatedTeam.id === this.activeTeamIndex
    );
  }

  private getTeamsForCurrentUserAsMember() {
    return this.teamService.getTeamsByCurrentMember().pipe(
      concatMap(teams => {
        this.teams = teams;
        if (teams.length > 0 && this.activeTeamIndex === null) {
          const [firstTeam] = this.teams;
          this.activeTeamIndex = firstTeam.id;
          SidebarComponent.activeTeamInitializer.next(this.activeTeamIndex);
          const pageableDefault = this.createPageableWithSort();
          this.searchCardsByTeamId(this.activeTeamIndex, pageableDefault);
        }
        return of(null);
      })
    );
  }

  private getCurrentUserTeam() {
    return this.teamService.getCurrentUserTeam().pipe(
      concatMap(team => {
        this.userCreatedTeam = team;
        this.handleTeamTransactionPermission(team)
        this.setActiveTeam(this.userCreatedTeam.id)
        this.editTeamNameForm.controls['newName'].setValue(team.name);
        return of(null);
      }),
      catchError((error: HttpErrorResponse) => {
        this.userCreatedTeam = null;
        this.itemsPerPage = 4;
        this.minPageCountForNavigation = 5;
        return of(null);
      })
    );
  }

  reloadCards() {
    const pageableDefault = new PageableDefault(this.currentCardPage, this.cardPageSize);
    this.searchCardsByTeamId(this.activeTeamIndex, pageableDefault);
  }

  searchCardsByTeamId(id: number, pageableDefault: PageableDefault) {
    if (id) {
      this.userCardService.searchCardsByTeamId(id, pageableDefault).subscribe(response => {
        this.pageUserCards = response;
        this.cards = response.content;
        const filteredItems = this.cards.find(i => i.userId === this.currentUserId);
        if (filteredItems) {
          this.activeSubscription = filteredItems.subscription
        }
        this.wrappedCards = this.cards.map(card => ({
          userCardResponse: card
        }));
        this.initFavorites();
        this.totalElementCount = response.totalElements;
        HeaderFactoryService.cardsCount.next(response.totalElements);
      });
    }
  }

  private handleTeamTransactionPermission(team: Team): void {
    if (team.requestAllowed == null) {
      const dialogRef = this.dialog.open(TeamTransactionPermissionModalComponent, {
        disableClose: true,
      });

      dialogRef.afterClosed().subscribe((result: boolean) => {
        if (result !== undefined) {
          this.teamService.updateRequestAllowed(team.id, result).subscribe();
        }
      });
    }
  }


  private searchCardsByAddingToExisting(id: number, pageableDefault: PageableDefault) {
    if (id) {
      this.userCardService.searchCardsByTeamId(id, pageableDefault).subscribe(
        {
          next: (response) => {
            this.pageUserCards = response;
            this.cards = [...this.cards, ...response.content];
            const newWrappedCards = response.content.map((card: any) => ({
              userCardResponse: card
            }));
            this.initFavorites();
            this.wrappedCards = [...this.wrappedCards, ...newWrappedCards];
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          }
        }
      )
    } else {
      this.isLoading = false;
    }
  }

  private subscribeToActiveTeamIndex(): void {
    this.subscription = this.activeTeamService.activeTeamIndex$.pipe().subscribe(index => {
      this.activeTeamIndex = index
      if (index) {
        const pageableDefault = this.createPageableWithSort()
        this.searchCardsByTeamId(index, pageableDefault);
      }
    });
  }

  private subscribeToListViewState(): void {
    this.viewStateService.isListView$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(isListView => {
        this.isListView = !isListView;
      });
  }

  private loadUserTeamsAndNavigate(): void {
    this.getCurrentUserTeam()
      .pipe(
        concatMap(() => this.getTeamsForCurrentUserAsMember()),
        concatMap(() => {
          if (this.userCreatedTeam == null && (this.teams && this.teams.length === 0)) {
            this.navigateByUrl('/cards');
            return of(null);
          }
          const teamIdParam = this.route.snapshot.queryParamMap.get('teamId');
          if (teamIdParam) {
            const teamId = Number(teamIdParam);
            const allTeams: Team[] = [
              ...(this.userCreatedTeam ? [this.userCreatedTeam] : []),
              ...(this.teams || [])
            ];
            const teamExists = allTeams.some(t => t.id === teamId);
            if (teamExists) {
              this.setActiveTeam(teamId);
            }
            this.router.navigate([], { queryParams: {}, replaceUrl: true });
          }
          return of(null);
        })
      )
      .subscribe();
  }

  private subscribeToDeleteNotification(): void {
    this.teamNotificationService.deleteNotification$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(cardId => {
        this.deleteCardFromTeam(cardId);
      });
  }

  private subscribeToChangeActivityNotification(): void {
    this.teamNotificationService.changeActivityNotification$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(({cardId}) => {
        this.changeCardActivity(cardId);
      });
  }

  private subscribeToHeaderData(): void {
    this.headerDataService.id$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(id => {
        this.currentUserId = id;
      });
  }

  private createPageableWithSort(): PageableDefault {
    const currentSort = CardsSortDialogService.sort.value;
    return new PageableDefault(
      this.currentCardPage,
      this.cardPageSize,
      CardsSortUtil.getSortFieldBySort(currentSort),
      CardsSortUtil.getSortDirectionBySort(currentSort)
    );
  }

  private initFavorites() {
    const ids = this.cards.map(card => card.id);
    this.favoriteCardsService.getFavorites(ids).subscribe(favorites => {
      this.isFavoriteMap = favorites;
    });
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBarUtil.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data
    });
  }

  private openSnackBar(message: string) {
    this.snackBar.open(message, '', {duration: 3000});
  }
}
